import React, { useCallback, useEffect, useRef, useState } from 'react';
import cityMaps from '../data/cityMaps.json';

/*
 * The illustration on the home page: a stencil street map of a city, picked at
 * random on arrival and then drifting on to the next city every few seconds,
 * each one dissolving into the last. No reload needed; it just keeps going.
 *
 * The maps are generated from open data by scripts/build-city-map.py and saved
 * as static SVGs listed in cityMaps.json. They are around half a megabyte each,
 * so the slideshow fetches exactly one city ahead and stops entirely when the
 * tab is hidden or the map is off screen. A visitor who never scrolls past the
 * hero pays for the handful of cities they actually saw.
 */

export const CITY_SLUGS = cityMaps.map((c) => c.slug);

const LAST_CITY_KEY = 'homeArt:lastCity';

// How long a city sits before the next one starts coming up, and how long the
// dissolve itself takes. FADE_MS is mirrored by .home-art-layer in index.css.
export const HOLD_MS = 6000;
export const FADE_MS = 630;

const readLastCity = () => {
  try {
    return window.localStorage.getItem(LAST_CITY_KEY);
  } catch {
    return null;
  }
};

const writeLastCity = (slug) => {
  try {
    window.localStorage.setItem(LAST_CITY_KEY, slug);
  } catch {
    // private mode or storage disabled: we just lose the no-repeat guarantee
  }
};

/**
 * Pick a city. A known `slug` pins that city; otherwise choose at random from
 * every city except `exclude` (the one shown last time).
 */
export const pickCity = (slug, exclude) => {
  const pinned = cityMaps.find((c) => c.slug === slug);
  if (pinned) return pinned;
  const pool = cityMaps.filter((c) => c.slug !== exclude);
  const options = pool.length > 0 ? pool : cityMaps;
  return options[Math.floor(Math.random() * options.length)];
};

/**
 * A fresh running order: every city but `exclude`, shuffled. Drawing from this
 * rather than picking at random each time means the tour visits all fourteen
 * cities before it repeats any of them.
 */
export const shuffledTour = (exclude) => {
  const deck = cityMaps.filter((c) => c.slug !== exclude);
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
};

const mapUrl = (city) => `${process.env.PUBLIC_URL}/${city.file}`;

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false; // jsdom and old browsers have no matchMedia
  }
};

export default function HomeArt({ city }) {
  const [chosen, setChosen] = useState(() => pickCity(city, readLastCity()));
  // The city on its way out, kept underneath for the length of the dissolve.
  const [outgoing, setOutgoing] = useState(null);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(() => !document.hidden);
  const figureRef = useRef(null);
  const tourRef = useRef([]);
  const pendingRef = useRef(null);

  // Remember what we showed so the next visit can avoid opening on it.
  useEffect(() => {
    writeLastCity(chosen.slug);
  }, [chosen]);

  // Browsers can restore a page from the back/forward cache with its old state
  // intact; treat that like a fresh arrival and pick again.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setChosen((current) => pickCity(city, current.slug));
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [city]);

  // Don't burn bandwidth on a tab nobody is looking at.
  useEffect(() => {
    const onVisibility = () => setTabVisible(!document.hidden);
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Likewise once the map has scrolled away. Below 900px the hero art is hidden
  // outright, so this also keeps phones from downloading maps they never show.
  useEffect(() => {
    const figure = figureRef.current;
    if (!figure || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(figure);
    return () => observer.disconnect();
  }, []);

  // The next city in the tour, refilling the running order when it runs dry.
  const takeNext = useCallback((current) => {
    if (tourRef.current.length === 0) tourRef.current = shuffledTour(current.slug);
    const next = tourRef.current.shift();
    return next && next.slug !== current.slug ? next : tourRef.current.shift() || next;
  }, []);

  // A pinned city stays put, and so does the map for anyone who has asked their
  // system for less motion.
  const running = !city && onScreen && tabVisible && !prefersReducedMotion();

  useEffect(() => {
    if (!running) return undefined;

    // Decide on the next city now and fetch it during the hold, so the dissolve
    // starts on the beat instead of waiting on the network. Holding the choice
    // in a ref means a pause and resume picks up where it left off.
    if (!pendingRef.current || pendingRef.current.slug === chosen.slug) {
      pendingRef.current = takeNext(chosen);
    }
    const next = pendingRef.current;

    let cancelled = false;
    let ready = false;
    let due = false;

    const swap = () => {
      if (cancelled || !ready || !due) return;
      pendingRef.current = null;
      setOutgoing(chosen);
      setChosen(next);
    };

    const loader = new Image();
    // A map that fails to load still takes its turn: the <img> shows its alt
    // text rather than freezing the slideshow on one city forever.
    loader.onload = () => { ready = true; swap(); };
    loader.onerror = () => { ready = true; swap(); };
    loader.src = mapUrl(next);
    if (loader.complete) { ready = true; }

    const holdTimer = setTimeout(() => { due = true; swap(); }, HOLD_MS);

    return () => {
      cancelled = true;
      clearTimeout(holdTimer);
      loader.onload = null;
      loader.onerror = null;
    };
  }, [chosen, running, takeNext]);

  // Retire the outgoing map once it has finished dissolving. This lives in its
  // own effect because swapping cities tears the one above down, and it would
  // take the timer with it.
  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = setTimeout(() => setOutgoing(null), FADE_MS);
    return () => clearTimeout(timer);
  }, [outgoing]);

  return (
    <figure className="home-art" ref={figureRef}>
      <div className="home-art-frame">
        {outgoing && (
          <img
            key={outgoing.slug}
            className="home-art-layer is-outgoing"
            src={mapUrl(outgoing)}
            alt=""
            aria-hidden="true"
            width={outgoing.width}
            height={outgoing.height}
          />
        )}
        <img
          key={chosen.slug}
          className="home-art-layer is-current"
          src={mapUrl(chosen)}
          alt={`A stencil street map of ${chosen.name}: water in dark ink, streets as fine lines`}
          width={chosen.width}
          height={chosen.height}
          loading="lazy"
        />
      </div>
      <figcaption className="art-caption">
        <span key={chosen.slug} className="art-caption-text">
          {chosen.name}. {chosen.coords}
        </span>
      </figcaption>
    </figure>
  );
}
